import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
  ElementRef,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import {
  copy_events_from_template,
  TemplateCopyInstance,
  buildMultiCopyFormDefaults,
} from '../../../services/component-data-dest/data/add-event';
import { add_template } from '../../../services/component-data-dest/data/add-template';
import { update_template } from '../../../services/component-data-dest/data/update-template';
import { delete_template } from '../../../services/component-data-dest/data/delete-template';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form.component';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { DUNA_TEMPLATES_EVENT } from '../../../services/component-data-source/data/templates-admin-events';
import { ConfirmDeleteDialogComponent } from '../duna-locations-and-rooms/confirm-delete-dialog.component';
import {
  eventFormLabelFromSeed,
  getEventUuidFromAppUrl,
  isOmittedFromTemplatePayload,
  stripEventFormForTemplateSeed,
} from './event-to-template.util';
import templateCreateForm from './template-create-form.json';
import templateCopyForm from './template-copy-form.json';
import { environment } from '../../../environment/environment';
import { normalizeEventFormForApi } from '../../../services/component-data-source/data/event/event-schedule-form.util';
import {
  collectSurveyValidationErrors,
  formatMissingFieldsMessage,
  validateEntireSurvey,
} from './survey-form-validation.util';

export type WizardStep = 1 | 2 | 3 | 4;
export type WizardMode = 'create' | 'edit' | 'copy';

const TEMPLATE_META_KEYS = ['template_name', 'template_description'];

export interface DunaTemplate {
  template_id: number;
  name: string;
  description?: string;
  visibility: string;
  template_data: Record<string, unknown>;
  created_at?: string;
}

@Component({
  selector: 'app-duna-template-to-events',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    DynamicFormComponent,
    DynamicTableComponent,
  ],
  templateUrl: './duna-template-to-events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-template-to-events.component.scss'],
})
export class DunaTemplateToEventsComponent implements OnInit, OnDestroy {
  @ViewChild('templateForm') templateForm?: DynamicFormComponent;
  @ViewChild('copyForm') copyForm?: DynamicFormComponent;
  @ViewChild('templatesTable') templatesTable?: DynamicTableComponent;

  wizardStep: WizardStep = 1;
  mode: WizardMode = 'create';
  selectedTemplate: DunaTemplate | null = null;
  templateFormData: Record<string, unknown> = {};
  copyFormData: Record<string, unknown> = {};
  isProcessing = false;
  /** Henter event fra URL før valgskærm vises. */
  eventUrlChoiceLoading = false;
  /** Valg mellem engangskopi / ny skabelon (før normal wizard-visning). */
  showEventUrlChoice = false;
  private pendingEventLabel = '';
  private pendingEventFormData: Record<string, unknown> | null = null;
  private eventUrlFlowStarted = false;

  readonly templatesDynamicInput = {
    dataSource: { type: 'text', value: 'templates' },
  };

  readonly templateFormSettings = {
    hideSubmitButton: true,
    showEmptyFormSubmit: false,
    showNavigationButtons: true,
    navigationButtonsLocation: 'topBottom',
    pagePrevText: 'Forrige',
    pageNextText: 'Næste',
    formDataSource: 'event_form_data',
  };

  readonly copyFormSettings = {
    hideSubmitButton: true,
    showEmptyFormSubmit: false,
    showNavigationButtons: false,
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private componentDataSourceService: ComponentDataSourceService,
    private componentDataDestService: ComponentDataDestService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.hideSiblingCmsPlaceholderNotices();
    window.addEventListener(
      DUNA_TEMPLATES_EVENT,
      this.onTemplatesEvent as EventListener
    );
    void this.initEventUrlChoiceIfNeeded();
  }

  /** Skjul midlertidige CMS-overskrifter placeret lige over komponenten på siden. */
  private hideSiblingCmsPlaceholderNotices(): void {
    const host = this.elementRef.nativeElement;
    let sibling: Element | null = host.previousElementSibling;
    while (sibling) {
      const text = (sibling.textContent || '').trim();
      if (
        /ENDNU IKKE FÆRDIG/i.test(text) ||
        /^OBS\.?\s*-/i.test(text)
      ) {
        (sibling as HTMLElement).hidden = true;
      }
      sibling = sibling.previousElementSibling;
    }
  }

  /** True when copying from URL event without a saved template (template_id 0). */
  get isEphemeralEventCopy(): boolean {
    return (
      this.mode === 'copy' &&
      (this.selectedTemplate?.template_id ?? 0) === 0
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      DUNA_TEMPLATES_EVENT,
      this.onTemplatesEvent as EventListener
    );
  }

  private onTemplatesEvent = (event: Event): void => {
    const detail = (event as CustomEvent).detail as {
      action: string;
      row: DunaTemplate;
    };
    if (!detail?.row) return;

    this.ngZone.run(() => {
      switch (detail.action) {
        case 'copy':
          void this.startCopy(detail.row);
          break;
        case 'edit':
          void this.startEdit(detail.row);
          break;
        case 'confirm-delete':
          this.confirmDelete(detail.row);
          break;
      }
    });
  };

  private notifyViewChanged(): void {
    this.cdr.detectChanges();
  }

  getTemplateCreateFormConfig(): typeof templateCreateForm {
    return templateCreateForm;
  }

  getTemplateCopyFormConfig(): typeof templateCopyForm {
    return templateCopyForm;
  }

  get stepLabels(): string[] {
    return ['Vælg skabelon', 'Definer skabelon', 'Opret events', 'Bekræft'];
  }

  goToStep(step: WizardStep): void {
    this.wizardStep = step;
    this.notifyViewChanged();
  }

  private async initEventUrlChoiceIfNeeded(): Promise<void> {
    if (this.eventUrlFlowStarted) return;
    const eventUuid = getEventUuidFromAppUrl(this.router);
    if (!eventUuid) return;

    this.eventUrlFlowStarted = true;
    this.eventUrlChoiceLoading = true;
    this.notifyViewChanged();

    let eventFormData: Record<string, unknown>;
    try {
      const obs = this.componentDataSourceService.executeDataSource(
        'event_form_data'
      );
      if (!obs) {
        throw new Error('event_form_data unavailable');
      }
      eventFormData = (await firstValueFrom(obs)) as Record<string, unknown>;
      if (!eventFormData || eventFormData['new_edit_copy'] === 'new') {
        throw new Error('invalid event');
      }
    } catch {
      this.eventUrlChoiceLoading = false;
      this.snackBar.open('Kunne ikke hente eventet', 'Luk', {
        duration: 4000,
      });
      this.notifyViewChanged();
      return;
    }

    this.ngZone.run(() => {
      this.pendingEventFormData = eventFormData;
      this.pendingEventLabel = eventFormLabelFromSeed(eventFormData);
      this.eventUrlChoiceLoading = false;
      this.showEventUrlChoice = true;
      this.notifyViewChanged();
    });
  }

  dismissEventUrlChoice(): void {
    this.showEventUrlChoice = false;
    this.pendingEventFormData = null;
    this.pendingEventLabel = '';
    this.wizardStep = 1;
    this.notifyViewChanged();
  }

  chooseEventUrlCopyOnce(): void {
    const data = this.pendingEventFormData;
    if (!data) return;
    this.showEventUrlChoice = false;
    this.startCopyFromEventForm(data, this.pendingEventLabel);
    this.pendingEventFormData = null;
    this.notifyViewChanged();
  }

  chooseEventUrlCreateTemplate(): void {
    const data = this.pendingEventFormData;
    if (!data) return;
    this.showEventUrlChoice = false;
    this.startCreateTemplateFromEventForm(data, this.pendingEventLabel);
    this.pendingEventFormData = null;
    this.notifyViewChanged();
  }

  private startCopyFromEventForm(
    eventFormData: Record<string, unknown>,
    eventLabel: string
  ): void {
    const templateData = stripEventFormForTemplateSeed(eventFormData);
    this.mode = 'copy';
    this.selectedTemplate = {
      template_id: 0,
      name: `Engangskopi: ${eventLabel}`,
      visibility: 'private',
      template_data: templateData,
    };
    this.copyFormData = { ...this.buildCopyFormDefaults(this.selectedTemplate) };
    this.wizardStep = 3;
  }

  private startCreateTemplateFromEventForm(
    eventFormData: Record<string, unknown>,
    eventLabel: string
  ): void {
    const templateData = stripEventFormForTemplateSeed(eventFormData);
    this.mode = 'create';
    this.selectedTemplate = null;
    this.templateFormData = {
      ...templateData,
      template_name: eventLabel,
      template_description: '',
      event_duration_type: 'single_day',
      visibility: templateData['visibility'] ?? 'private',
      event_active: templateData['event_active'] ?? 'active',
      is_shown: templateData['is_shown'] ?? 'shown',
      waitlist: templateData['waitlist'] ?? 'room_size',
      catering: templateData['catering'] ?? 'irrelevant',
      destination_period: templateData['destination_period'] ?? 'current_period',
    };
    this.wizardStep = 2;
  }

  startCreate(): void {
    this.mode = 'create';
    this.selectedTemplate = null;
    this.templateFormData = {
      event_duration_type: 'single_day',
      visibility: 'private',
      event_active: 'active',
      is_shown: 'shown',
      waitlist: 'room_size',
      catering: 'irrelevant',
      destination_period: 'current_period',
    };
    this.wizardStep = 2;
    this.notifyViewChanged();
  }

  async startEdit(row: DunaTemplate): Promise<void> {
    this.mode = 'edit';
    try {
      const full = await this.fetchTemplate(row.template_id);
      this.selectedTemplate = full;
      this.templateFormData = { ...this.buildTemplateFormData(full) };
      this.wizardStep = 2;
    } catch {
      this.snackBar.open('Kunne ikke hente skabelonen', 'Luk', {
        duration: 4000,
      });
    } finally {
      this.notifyViewChanged();
    }
  }

  async startCopy(row: DunaTemplate): Promise<void> {
    this.mode = 'copy';
    try {
      const full = await this.fetchTemplate(row.template_id);
      this.selectedTemplate = full;
      this.copyFormData = { ...this.buildCopyFormDefaults(full) };
      this.wizardStep = 3;
    } catch {
      this.snackBar.open('Kunne ikke hente skabelonen', 'Luk', {
        duration: 4000,
      });
    } finally {
      this.notifyViewChanged();
    }
  }

  backToList(): void {
    this.wizardStep = 1;
    this.selectedTemplate = null;
    this.notifyViewChanged();
    this.refreshTemplatesTable();
  }

  private refreshTemplatesTable(): void {
    setTimeout(() => {
      this.templatesTable?.refreshTable();
      this.cdr.markForCheck();
    }, 0);
  }

  private async fetchTemplate(templateId: number): Promise<DunaTemplate> {
    const template = await firstValueFrom(
      this.http.get<DunaTemplate>(
        `${environment.apiUrl}/duna_templates/${templateId}`
      )
    );
    if (typeof template.template_data === 'string') {
      try {
        template.template_data = JSON.parse(template.template_data);
      } catch {
        template.template_data = {};
      }
    }
    return template;
  }

  private buildTemplateFormData(
    template: DunaTemplate
  ): Record<string, unknown> {
    const data = template.template_data || {};
    return {
      ...data,
      event_duration_type: 'single_day',
      template_name: template.name,
      template_description: template.description || '',
    };
  }

  private buildCopyFormDefaults(
    template: DunaTemplate
  ): Record<string, unknown> {
    return { ...buildMultiCopyFormDefaults(template.template_data || {}) };
  }

  extractTemplatePayload(formData: Record<string, unknown>): {
    name: string;
    description: string;
    visibility: string;
    template_data: Record<string, unknown>;
    template_id?: number;
  } {
    const template_data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (
        !TEMPLATE_META_KEYS.includes(key) &&
        !isOmittedFromTemplatePayload(key)
      ) {
        template_data[key] = value;
      }
    }
    template_data['event_duration_type'] = 'single_day';
    const normalizedTemplateData = normalizeEventFormForApi(template_data);

    const payload = {
      name: String(formData['template_name'] || '').trim(),
      description: String(formData['template_description'] || '').trim(),
      visibility:
        this.mode === 'edit' && this.selectedTemplate?.visibility
          ? this.selectedTemplate.visibility
          : 'private',
      template_data: normalizedTemplateData,
    };

    if (this.selectedTemplate?.template_id) {
      return { ...payload, template_id: this.selectedTemplate.template_id };
    }
    return payload;
  }

  private getTemplateFormValues(): Record<string, unknown> {
    return ((
      this.templateForm?.surveyModel as { data?: Record<string, unknown> }
    )?.data || this.templateFormData) as Record<string, unknown>;
  }

  private getCopyFormValues(): Record<string, unknown> {
    return ((this.copyForm?.surveyModel as { data?: Record<string, unknown> })
      ?.data || this.copyFormData) as Record<string, unknown>;
  }

  private showValidationSnack(message: string): void {
    this.snackBar.open(message, 'Luk', {
      duration: Math.min(12000, 4000 + message.length * 40),
      panelClass: ['error-snackbar'],
    });
  }

  private validateSurveyForm(
    form: DynamicFormComponent | undefined
  ): boolean {
    const model = form?.surveyModel;
    if (!model) {
      return false;
    }
    if (validateEntireSurvey(model)) {
      return true;
    }
    this.showValidationSnack(
      formatMissingFieldsMessage(collectSurveyValidationErrors(model))
    );
    return false;
  }

  validateTemplateForm(): boolean {
    if (!this.validateSurveyForm(this.templateForm)) {
      return false;
    }
    const data = this.getTemplateFormValues();
    if (!String(data['template_name'] || '').trim()) {
      this.showValidationSnack('Manglende felter: Skabelonnavn');
      return false;
    }
    return true;
  }

  private persistCopyFormData(): void {
    const values = this.getCopyFormValues();
    const instances = values['copy_instances'];
    this.copyFormData = {
      ...values,
      copy_instances: Array.isArray(instances)
        ? (instances as TemplateCopyInstance[]).map((row) => ({ ...row }))
        : [],
    };
  }

  validateCopyForm(): boolean {
    if (!this.validateSurveyForm(this.copyForm)) {
      return false;
    }
    const instances = this.getCopyInstances();
    if (!instances.length) {
      this.showValidationSnack('Tilføj mindst én dato');
      return false;
    }
    return true;
  }

  getCopyInstances(): TemplateCopyInstance[] {
    const data = this.getCopyFormValues();
    const raw = data['copy_instances'];
    const instances = Array.isArray(raw) ? (raw as TemplateCopyInstance[]) : [];
    const description = String(data['description'] || '').trim();
    const shortDescription = String(data['short_description'] || '').trim();
    return instances.map((instance) => ({
      ...instance,
      description,
      short_description: shortDescription,
    }));
  }

  async saveTemplate(andCopy = false): Promise<void> {
    if (!this.validateTemplateForm()) return;

    this.isProcessing = true;
    try {
      const payload = this.extractTemplatePayload(this.getTemplateFormValues());
      if (!payload.name) {
        this.snackBar.open('Skabelonnavn er påkrævet', 'Luk', {
          duration: 3000,
        });
        return;
      }

      if (this.mode === 'edit' && payload.template_id) {
        await firstValueFrom(
          update_template(
            this.componentDataDestService,
            this.componentDataSourceService,
            payload
          )
        );
        this.selectedTemplate = {
          ...(this.selectedTemplate as DunaTemplate),
          name: payload.name,
          description: payload.description,
          visibility: payload.visibility,
          template_data: payload.template_data,
        };
        this.snackBar.open('Skabelon opdateret', 'Luk', { duration: 3000 });
      } else {
        const response = await firstValueFrom(
          add_template(
            this.componentDataDestService,
            this.componentDataSourceService,
            payload
          )
        );
        const createdId = (response as { template_id?: number })?.template_id;
        this.selectedTemplate = {
          template_id: createdId || 0,
          name: payload.name,
          description: payload.description,
          visibility: payload.visibility,
          template_data: payload.template_data,
        };
        this.mode = 'edit';
        this.snackBar.open('Skabelon oprettet', 'Luk', { duration: 3000 });
      }

      if (andCopy) {
        this.copyFormData = {
          ...this.buildCopyFormDefaults(this.selectedTemplate!),
        };
        this.wizardStep = 3;
      } else {
        this.backToList();
      }
    } catch (error) {
      console.error('[saveTemplate]', error);
      this.snackBar.open('Kunne ikke gemme skabelonen', 'Luk', {
        duration: 4000,
      });
    } finally {
      this.isProcessing = false;
      this.notifyViewChanged();
    }
  }

  proceedToConfirm(): void {
    if (!this.selectedTemplate) {
      this.snackBar.open('Vælg en skabelon først', 'Luk', { duration: 3000 });
      return;
    }
    if (!this.validateCopyForm()) return;
    this.persistCopyFormData();
    this.wizardStep = 4;
    this.notifyViewChanged();
  }

  async createEvents(): Promise<void> {
    if (!this.selectedTemplate?.template_data) return;

    this.isProcessing = true;
    try {
      const instances = this.getCopyInstances();
      await copy_events_from_template(
        this.componentDataDestService,
        this.componentDataSourceService,
        this.selectedTemplate.template_data,
        instances
      );
      this.snackBar.open(`${instances.length} event(s) oprettet`, 'Luk', {
        duration: 4000,
      });
      this.copyFormData = {};
      this.backToList();
    } catch (error) {
      console.error('[createEvents]', error);
      this.snackBar.open('Fejl ved oprettelse af events', 'Luk', {
        duration: 4000,
      });
    } finally {
      this.isProcessing = false;
      this.notifyViewChanged();
    }
  }

  confirmDelete(row: DunaTemplate): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: {
        title: 'Slet skabelon',
        message: `Er du sikker på, at du vil slette skabelonen "${row.name}"?`,
      },
    });

    ref.afterClosed().subscribe(async (confirmed) => {
      if (!confirmed) return;
      try {
        await firstValueFrom(
          delete_template(
            this.componentDataDestService,
            this.componentDataSourceService,
            {
              template_id: row.template_id,
            }
          )
        );
        this.snackBar.open('Skabelon slettet', 'Luk', { duration: 3000 });
        if (this.selectedTemplate?.template_id === row.template_id) {
          this.backToList();
        } else {
          this.refreshTemplatesTable();
        }
      } catch {
        this.snackBar.open('Kunne ikke slette skabelonen', 'Luk', {
          duration: 4000,
        });
      } finally {
        this.notifyViewChanged();
      }
    });
  }

  formatDateTime(value: string | undefined): string {
    if (!value) return '—';
    if (value.includes('T')) {
      const [datePart, timePart] = value.split('T');
      const [y, m, d] = datePart.split('-');
      const time = (timePart || '').slice(0, 5);
      return `${d}/${m}/${y} ${time}`;
    }
    return value;
  }
}
